// Email sending functions - MIGRATED TO OUTREACH API
// This module now uses the secure Outreach API instead of direct Mailgun calls
// Original implementation backed up as messaging.ts.backup

import type {
  SendEmailOptions,
  CampaignStats,
  IndividualEmailSendResponse
} from '../types';
import { 
  sendEmails as apiSendEmails,
  sendMailgunEmail as apiSendTemplateEmail,
  getMailgunCampaignStats as apiGetCampaignStats,
  type CampaignStatsRequestOptions
} from '../api/outreachApiClient';

/**
 * Send emails using Outreach API (replaces direct Mailgun API calls)
 *
 * @param options Email options including recipients, subject, body, and optional campaignId for tracking
 * @returns Object with success status and message
 */
export const sendEmails = async (
  options: SendEmailOptions & { campaignId?: string }
): Promise<IndividualEmailSendResponse> => {
  try {
    console.log('Sending emails via Outreach API:', {
      recipientCount: options.recipients.length,
      testMode: options.testMode,
      overrideToAddress: !!options.overrideToAddress,
      campaignId: options.campaignId
    });

    // Use the new Outreach API client
    const result = await apiSendEmails(options);

    // The API client handles message correlation internally,
    // so we don't need to manually store correlations here anymore
    console.log('Email sending result:', result);

    return result;
  } catch (error) {
    console.error("Error sending email via Outreach API:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Unknown error sending email",
      errors: [error instanceof Error ? error.message : "Unknown error"],
    };
  }
};

/**
 * Send emails using Outreach API template endpoint
 * Uses the secure backend API instead of direct Mailgun template calls
 *
 * @param options Email options including recipients, subject, body, and optional campaignId for tracking
 * @returns Object with success status and message
 */
export const sendMailgunEmail = async (options: {
  recipients: Array<{ email: string; name?: string }>;
  subject: string;
  body: string;
  overrideToAddress?: string;
  campaignId?: string;
  tags?: string[];
}): Promise<IndividualEmailSendResponse> => {
  try {
    console.log('Sending template email via Outreach API:', {
      recipientCount: options.recipients.length,
      overrideToAddress: !!options.overrideToAddress,
      campaignId: options.campaignId,
      tagCount: options.tags?.length ?? 0
    });

    // Use the new Outreach API client
    const result = await apiSendTemplateEmail(options);

    // The API client handles message correlation internally,
    // so we don't need to manually store correlations here anymore
    console.log('Template email sending result:', result);

    return result;
  } catch (error) {
    console.error("Error sending template email via Outreach API:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Unknown error sending template email",
      errors: [error instanceof Error ? error.message : "Unknown error"],
    };
  }
};

/**
 * Get campaign statistics from Outreach API
 * Uses the secure backend API instead of direct Mailgun stats calls
 * 
 * @param campaignId The campaign ID to get statistics for
 * @returns Campaign statistics including delivery, open, and click rates
 */
export const getMailgunCampaignStats = async (
  campaignId: string,
  options: CampaignStatsRequestOptions = {}
): Promise<CampaignStats> => {
  try {
    console.log(
      `Fetching campaign statistics via Outreach API for campaign ID: ${campaignId}`,
      options.provider ? `provider=${options.provider}` : ''
    );
    
    // Use the new Outreach API client
    const result = await apiGetCampaignStats(campaignId, options);

    console.log(`Successfully retrieved campaign statistics for ${campaignId} via Outreach API:`, {
      totalSent: result.totalSent,
      deliveryRate: `${result.deliveryRate}%`,
      openRate: `${result.openRate}%`,
      clickRate: `${result.clickRate}%`,
      provider: result.provider,
    });

    return result;
  } catch (error) {
    console.error(`Error getting campaign statistics for ${campaignId} via Outreach API:`, error);
    
    if (error instanceof Error) {
      throw new Error(`Failed to get campaign statistics: ${error.message}`);
    }
    throw new Error('Unknown error occurred while fetching campaign statistics');
  }
};

// Export legacy function names for backward compatibility
export {
  sendEmails as sendEmailsViaOutreach,
  sendMailgunEmail as sendTemplateEmailViaOutreach,
  getMailgunCampaignStats as getCampaignStatsViaOutreach
};
